import type { WithActiveBranchId } from '@app/api/shared';
import type { IBaseQueryParams } from '@app/services/rtkQuery/baseQuery';

export enum ClientServicesEndpoints {
  GetClientServices = 'GetClientServices',
  GetClientServicesByEmployeeId = 'GetClientServicesByEmployeeId',
  SearchClientServices = 'SearchClientServices',
  GetBranchClientServices = 'GetBranchClientServices',
  GetClientServiceById = 'GetClientServiceById',
  GetClientServiceCategories = 'GetClientServiceCategories',
  GetClientServiceCategoriesForFilter = 'GetClientServiceCategoriesForFilter',
  GetClientServiceCategoryById = 'GetClientServiceCategoryById',
  UpdateClientServiceCategory = 'UpdateClientServiceCategory',
  UpdateClientService = 'UpdateClientService',
  SetBranchClientServicesOrder = 'SetBranchClientServicesOrder',
  GetEmployeeClientServices = 'GetEmployeeClientServices',
  UpdateEmployeeClientServices = 'UpdateEmployeeClientServices',
  GetClientServiceEmployees = 'GetClientServiceEmployees',
  UpdateClientServiceEmployees = 'UpdateClientServiceEmployees',
  ExportServicesToExcelByIds = 'ExportServicesToExcelByIds',
  ImportExcel = 'ImportExcel',
  StopImportExcel = 'StopImportExcel',
  UnarchiveClientService = 'UnarchiveClientService',
}

export type SalaryFormat = 'Percentage' | 'FixedRate';

export interface ModelClientServiceCategory {
  Id: number;
  Name: string | null;
  Description: string | null;
  BranchId: number;
  IsDeleted: boolean;
  MigrateId: number | null;
}

export interface ModelClientServiceAddition {
  Id: number;
  ServiceId: number;
  AdditionServiceId: number;
  Name: string | null;
}

export interface ModelClientService {
  Id: number;
  Name: string | null;
  PublicName?: string | null;
  PriceS: number;
  PriceE: number | null;
  Duration: number;
  ServiceType: 'individual' | 'group' | null;
  CategoryId: number | null;
  Category: string | null;
  BranchId: number;
  IsDeleted: boolean;
  CountEmployees: number;
  Description: string | null;
  ClientServiceAdditions: ModelClientServiceAddition[] | null;
  SalaryFormat: SalaryFormat;
  SalaryAmount: number;
  MedicalServiceCode?: string | null;
}

export interface ModelBranchClientService {
  Id: number;
  Name: string | null;
  CategoryId: number;
  CategoryName: string | null;
  IsCategoryDeleted: boolean;
  CategoryDescription: string | null;
  OrderIndex: number;
  PriceS: number;
  PriceE: number | null;
  Duration: number;
  CountEmployees: number;
  IsDeleted: boolean;
}

export interface ModelClientServiceEmployee {
  Id: number;
  UserId: number;
  ServiceId: number;
  Duration: number;
}

export interface ModelClientServiceEmployeeLite {
  ServiceId: number;
  Duration: number;
  SalaryFormat: SalaryFormat;
  SalaryAmount: number;
}

export interface ModelClientServiceOrder {
  ClientServiceId: number;
  ServiceCategoryId: number;
  OrderIndex: number;
}

export interface ModelClientServiceFilter {
  SearchTerm: string | null;
  CategoryIds: number[] | null;
  BranchIds: number[] | null;
}

export interface ModelClientServicesFile {
  Id: number;
  Name: string | null;
  Data: string | null;
  Lifetime: string | null;
  Description: string | null;
}

export type GetClientServicesRequest = WithActiveBranchId;
export type GetClientServicesByEmployeeIdRequest = WithActiveBranchId<{
  employeeId: number;
}>;
export type SearchClientServicesRequest = WithActiveBranchId<{
  data: ModelClientServiceFilter;
}>;
export type GetBranchClientServicesRequest = WithActiveBranchId<{
  logs?: IBaseQueryParams['logs'];
}>;
export type GetClientServiceByIdRequest = WithActiveBranchId<{
  serviceId: number;
}>;
export type GetClientServiceCategoriesRequest = WithActiveBranchId;
export type GetClientServiceCategoriesForFilterRequest = WithActiveBranchId<{
  logs?: IBaseQueryParams['logs'];
}>;
export type GetClientServiceCategoryByIdRequest = WithActiveBranchId<{
  categoryId: number;
}>;
export type UpdateClientServiceCategoryRequest = WithActiveBranchId<{
  data: ModelClientServiceCategory;
}>;
export type UpdateClientServiceRequest = WithActiveBranchId<{
  data: ModelClientService;
}>;
export type SetBranchClientServicesOrderRequest = WithActiveBranchId<{
  data: ModelClientServiceOrder[];
}>;
export type GetEmployeeClientServicesRequest = WithActiveBranchId<{
  employeeId: number;
}>;
export type UpdateEmployeeClientServicesRequest = WithActiveBranchId<{
  employeeId: number;
  data: ModelClientServiceEmployeeLite[];
}>;
export type GetClientServiceEmployeesRequest = WithActiveBranchId<{
  serviceId: number;
}>;
export type UpdateClientServiceEmployeesRequest = WithActiveBranchId<{
  serviceId: number;
  data: ModelClientServiceEmployee[];
}>;
export type ExportServicesToExcelByIdsRequest = WithActiveBranchId<{
  data: number[];
}>;
export type ImportExcelRequest = WithActiveBranchId<{
  extension: string;
  data: string;
}>;
export type StopImportExcelRequest = WithActiveBranchId;
export type UnarchiveClientServiceRequest = WithActiveBranchId<{
  serviceId: number;
}>;

export type GetClientServicesResponse = ModelClientService[];
export type GetClientServicesByEmployeeIdResponse = ModelClientService[];
export type SearchClientServicesResponse = ModelClientService[];
export type GetBranchClientServicesResponse = ModelBranchClientService[];
export type GetClientServiceByIdResponse = ModelClientService;
export type GetClientServiceCategoriesResponse = ModelClientServiceCategory[];
export type GetClientServiceCategoriesForFilterResponse =
  ModelClientServiceCategory[];
export type GetClientServiceCategoryByIdResponse = ModelClientServiceCategory;
export type UpdateClientServiceCategoryResponse = ModelClientServiceCategory;
export type UpdateClientServiceResponse = ModelClientService;
export type SetBranchClientServicesOrderResponse = void;
export type GetEmployeeClientServicesResponse = ModelClientService[];
export type UpdateEmployeeClientServicesResponse = void;
export type GetClientServiceEmployeesResponse = ModelClientServiceEmployee[];
export type UpdateClientServiceEmployeesResponse = void;
export type ExportServicesToExcelByIdsResponse = ModelClientServicesFile;
export type ImportExcelResponse = void;
export type StopImportExcelResponse = void;
export type UnarchiveClientServiceResponse = void;
